import { ActionReducer, ActionReducerMap, MetaReducer } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';
import { State } from './core.state';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

import * as CoursesReducers from './course/course.reducer';
import * as UsersReducers from './users/users.reducer';
import * as ResourceReducers from './resource/resource.reducer';
import * as OrderReducers from './order/order.reducer';
import * as LiveClassReducers from './live-class/live-class.reducer';
import * as ConfigReducer from './config/config.reducer';
import * as userSessionsReducer from './user-sessions/sessions.reducer';
import * as UserActions from './users/user.actions';
import * as OrderActions from './order/order.actions';

export const reducers: ActionReducerMap<State> = {
  courses: CoursesReducers.reducer,
  users: UsersReducers.reducer,
  resources: ResourceReducers.reducer,
  orders: OrderReducers.reducer,
  liveClass: LiveClassReducers.reducer,
  config: ConfigReducer.reducer,
  userSessions: userSessionsReducer.reducer,
};

// Web-based meta reducer using localStorage
export function webStorageSyncReducer(
  reducer: ActionReducer<any>
): ActionReducer<any> {
  return localStorageSync({ keys: ['users', 'orders'], rehydrate: true })(
    reducer
  );
}

// Flag to prevent sync before rehydration completes (native platform only)
let isRehydrationComplete = false;

// Native application friendly meta reducer
// Uses Capacitor Preferences API for cross-platform compatibility
// Handles sync-to-storage only. Rehydration must be handled separately via initialization.
export function nativeAppSyncReducer(
  reducer: ActionReducer<State>
): ActionReducer<State> {
  const keysToSync: (keyof State)[] = ['users', 'orders'];

  return (state: State | undefined, action): State => {
    const result = reducer(state, action);

    // Only sync after rehydration is complete to avoid overwriting stored state
    if (result && isRehydrationComplete) {
      // Sync state to Capacitor Preferences after each action
      keysToSync.forEach((key) => {
        if (result[key]) {
          Preferences.set({
            key: `app_state_${key}`,
            value: JSON.stringify(result[key]),
          }).catch((err) => {
            console.error(`Error syncing ${key} to Preferences:`, err);
          });
        }
      });
    }

    return result;
  };
}

// Rehydrate native app state from Preferences
// Call this during app initialization (e.g., in app.component.ts ngOnInit or APP_INITIALIZER)
export async function rehydrateNativeAppState(store: any): Promise<void> {
  const keysToSync = ['users', 'orders'];

  for (const key of keysToSync) {
    try {
      const result = await Preferences.get({ key: `app_state_${key}` });
      if (result?.value) {
        const state = JSON.parse(result.value);
        console.log(`Rehydrated ${key} from Preferences.`);
        console.log(`Rehydrated data for ${key} -> `, state);

        // Dispatch appropriate rehydration action
        if (key === 'users') {
          store.dispatch(UserActions.rehydrateUserState({ userState: state }));
        } else if (key === 'orders') {
          store.dispatch(
            OrderActions.rehydrateOrderState({ orderState: state })
          );
        }
      }
    } catch (err) {
      console.error(`Error rehydrating ${key}:`, err);
    }
  }

  // Mark rehydration as complete - sync can now proceed
  isRehydrationComplete = true;
  console.log('Native state rehydration complete. Sync enabled.');
}

export const metaReducers: MetaReducer<State>[] = [];

// Conditionally apply the appropriate meta reducer based on environment
if (typeof window !== 'undefined') {
  if (Capacitor.isNativePlatform()) {
    metaReducers.push(nativeAppSyncReducer);
  } else {
    metaReducers.push(webStorageSyncReducer);
  }
}
